// Email reminder subscriptions — the modal surface (docs/reminders.md), titled
// **Custom Notifications** since 2026-10-14 (owner; *Email Reminders* until then).
// Rendered inside a Dialog off the header's mail icon (Upload's pattern).
// Lists the user's subscriptions (add / edit / pause / remove) and an add/edit
// form whose steps are filtered to what the user can act on. Editing is
// copy-on-write, done in one server call (lib/reminders). The form opens on its
// Type — Scheduled or Triggered (owner, 2026-10-21 — D-391) — and shows the
// fields under it once one is chosen.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  FieldContextProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  ToggleButton,
  Toast,
  ToastTitle,
  useToastController,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  useFieldContext_unstable,
} from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import type { Session } from '../lib/identity'
import type { ReminderSubscription } from '../lib/types'
import { TIMELINE_NODES, TOASTER_ID } from '../lib/constants'
import { loadMyReminders } from '../lib/data'
import {
  DAY_TOKENS,
  KIND_OPTIONS,
  searchTimeOptions,
  timeLabel,
  stepOptions,
  isActiveSubscription,
  formatSchedule,
  emptyForm,
  rowToForm,
  validateForm,
  saveNewSubscription,
  saveEditedSubscription,
  cancelSubscription,
  type NotificationKind,
  type ReminderForm,
} from '../lib/reminders'
import { DecisionDialog } from '../components/DecisionDialog'
import { EntityPicker } from '../components/EntityPicker'
import { StatusDot } from '../components/StatusDot'
import { StatusToggle } from '../components/StatusToggle'
import { rowActivationProps, useClickableRowClass, useRoomyTableClass } from '../components/tableDensity'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 },
  // THE LIST IS A TABLE (owner, 2026-10-14: "attempt a redesign of the list of
  // already-established alerts"), as every other list in the app is: one row
  // per notification, the row opens it, and the columns say what it is, when it goes
  // and what it covers without opening it. It was a stack of cards reading
  // "Mon, Tue, Wed, Thu, Fri at 9:00 AM · from 2026-10-14" and "3 steps".
  table: { tableLayout: 'fixed' },
  stepList: { display: 'flex', flexDirection: 'column' },
  rowActions: { display: 'flex', gap: '2px', justifyContent: 'flex-end' },
  // The one action under the list, at the right like every pop-up's buttons.
  listActions: { display: 'flex', justifyContent: 'flex-end' },
  daysRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  // ToggleButton's own "checked" look is a near-invisible neutral tint
  // (`colorNeutralBackground1Selected`, same family as the unchecked
  // background) — selected vs. not is unmistakable only once this wins.
  // Passed last into `mergeClasses` (never a template join, per
  // app/README.md's Fluent UI v9 gotchas), so Griffel's own property-level
  // merge lets it override the built-in checked/hover rules deterministically
  // rather than racing them on CSS specificity. `shorthands.borderColor`
  // (not a plain `borderColor` key) so the override lands on the same
  // per-side properties Fluent's own checked style sets.
  dayChecked: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackgroundPressed,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackgroundPressed),
    },
  },
  nameInput: { width: '100%', minWidth: 0 },
  checks: { display: 'flex', flexDirection: 'column', gap: '2px' },
  // The Active/Inactive toggle at the left, Cancel and Save at the right, as on
  // every References and Rules editor (D-201).
  formActions: { display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' },
  formActionsEnd: { display: 'flex', gap: '8px', marginLeft: 'auto' },
  empty: { color: tokens.colorNeutralForeground3, padding: '8px 0' },
})

/**
 * EACH STEP CHECKBOX IS NAMED BY ITS OWN STEP (QA 2026-09-30, T-01). A Fluent
 * `Field` hands its control id, its label and its hint to EVERY control inside
 * it, so all eight checkboxes shared one id, and the Field's label plus each
 * checkbox's own label pointed at the first of them: a screen reader heard
 * "Reminder Steps" eight times. The checkboxes now sit outside the Field's
 * control context, each with its own id and label, and the group carries the
 * Field's label and hint instead. The Field itself, its required marker and
 * its hint line are unchanged (D-234, D-236).
 */
function StepCheckGroup({ className, children }: { className: string; children: ReactNode }) {
  const field = useFieldContext_unstable()
  return (
    <div className={className} role="group" aria-labelledby={field?.labelId} aria-describedby={field?.hintId}>
      <FieldContextProvider value={undefined}>{children}</FieldContextProvider>
    </div>
  )
}

export function RemindersScreen({ session }: { session: Session }) {
  const s = useStyles()
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const [subs, setSubs] = useState<ReminderSubscription[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'list' | 'form'>('list')
  const [editing, setEditing] = useState<ReminderSubscription | null>(null)
  const [form, setForm] = useState<ReminderForm>(() => emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  /** The edit form's Active/Inactive toggle (D-361). A new notification is Active. */
  const [active, setActive] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const stepOpts = useMemo(() => stepOptions(session), [session])

  // This screen renders inside App.tsx's own reminders Dialog (never as a
  // standalone page), so the time picker below needs the same fix every
  // other in-dialog Fluent popup on this project needs (app/README.md's
  // Fluent UI v9 gotcha: an unmounted popup "closes as fast as it opens"
  // without an explicit `mountNode`/`inlinePopup`) — resolved the same way
  // `AssociatedEntitiesEditor` resolves its own dialog host, via `.closest`
  // rather than a prop threaded down from App.tsx, since this component has
  // no other reason to know about its parent dialog.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogHost, setDialogHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setDialogHost(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [mode])

  // The API scopes the list to the caller, so there is nothing to pass.
  const reload = useCallback(async () => {
    setError(null)
    try {
      setSubs(await loadMyReminders())
    } catch (e) {
      setError(errorMessage(e))
      setSubs([])
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const toast = (msg: string) =>
    dispatchToast(<Toast><ToastTitle>{msg}</ToastTitle></Toast>, { intent: 'success' })

  const openNew = () => {
    setEditing(null)
    setForm(emptyForm())
    setActive(true)
    setFormError(null)
    setMode('form')
  }
  const openEdit = (row: ReminderSubscription) => {
    setEditing(row)
    // A step the user no longer holds is not offered, so it is not kept either
    // (D-392); the tick takes it off the stored row too.
    const loaded = rowToForm(row)
    setForm({ ...loaded, steps: loaded.steps.filter((key) => stepOpts.some((o) => o.key === key)) })
    setActive(isActiveSubscription(row))
    setFormError(null)
    setMode('form')
  }

  const save = async () => {
    const err = validateForm(form)
    if (err) {
      setFormError(err)
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      if (editing) await saveEditedSubscription(editing, form, active)
      else await saveNewSubscription(form)
      toast(editing ? 'The custom notification was updated.' : 'The custom notification was created.')
      setMode('list')
      setEditing(null)
      await reload()
    } catch (e) {
      setFormError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const rowAction = async (row: ReminderSubscription, fn: () => Promise<void>, msg: string) => {
    setBusyId(row.id)
    try {
      await fn()
      toast(msg)
      await reload()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  /** THE REMINDER WAITING ON "Are you sure?" (QA wave B, 2026-09-25, B-07;
      `docs/decisions.md` D-171). Remove wrote on one click, and nothing on
      screen brings a removed reminder back. `removingShown` keeps the pop-up's
      words still while it closes — `HoldControl`'s `shown` pattern. */
  const [removing, setRemoving] = useState<ReminderSubscription | null>(null)
  const [removingShown, setRemovingShown] = useState<ReminderSubscription | null>(null)
  const confirmRemove = async () => {
    const row = removing
    if (!row) return
    await rowAction(row, () => cancelSubscription(row), 'The custom notification was removed.')
    setRemoving(null)
  }

  const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  // ---- form view ----
  if (mode === 'form') {
    // TITLE-CASE LABELS (D-236). The
    // "(optional)" markers went with the move to titles; the four fields
    // `validateForm` insists on carry the required marker instead, so which
    // ones may be left blank is still on screen.
    const stepChecks =
      stepOpts.length === 0 ? (
        <Text className={s.empty}>You have no steps you can act on.</Text>
      ) : (
        <StepCheckGroup className={s.checks}>
          {stepOpts.map((o) => (
            <Checkbox
              key={o.key}
              label={o.label}
              checked={form.steps.includes(o.key)}
              onChange={() => setForm({ ...form, steps: toggle(form.steps, o.key) })}
            />
          ))}
        </StepCheckGroup>
      )
    const kindLabel = KIND_OPTIONS.find((o) => o.value === form.kind)?.label ?? ''
    return (
      <div className={s.root} ref={rootRef}>
        {/* THE TYPE FIRST (owner, 2026-10-21 — D-391): Scheduled sends a digest
            on its days at its time; Triggered sends an email when an invoice
            arrives at one of its steps. Nothing under it shows until it is chosen. */}
        <Field label="Type" required>
          <Dropdown
            placeholder="Choose a type"
            mountNode={dialogHost ?? undefined}
            selectedOptions={form.kind === '' ? [] : [form.kind]}
            value={kindLabel}
            onOptionSelect={(_, d) => setForm({ ...form, kind: (d.optionValue as NotificationKind | undefined) ?? '' })}
          >
            {KIND_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value} text={o.label}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        {form.kind !== '' && (
          <Field label="Title">
            <Input
              className={s.nameInput}
              value={form.title}
              placeholder="e.g. Morning approvals"
              onChange={(_, d) => setForm({ ...form, title: d.value })}
            />
          </Field>
        )}

        {form.kind === 'scheduled' && (
          <>
            <Field label="Days Of The Week" required>
              <div className={s.daysRow}>
                {DAY_TOKENS.map((day) => (
                  <ToggleButton
                    key={day}
                    size="small"
                    checked={form.days.includes(day)}
                    className={mergeClasses(form.days.includes(day) && s.dayChecked)}
                    onClick={() => setForm({ ...form, days: toggle(form.days, day) })}
                  >
                    {day}
                  </ToggleButton>
                ))}
              </div>
            </Field>

            {/* QUARTER HOURS, SEARCHABLE (owner, 2026-10-14) — the digest goes out
                on a fifteen-minute tick, so these are the only times it can keep.
                Typing `830` or `8:30` finds 8:30 AM. */}
            <Field label="Time Of Day (Eastern)" required>
              <EntityPicker
                ariaLabel="Time Of Day (Eastern)"
                value={{ id: form.time, name: timeLabel(form.time) }}
                mountNode={dialogHost}
                search={(term) => Promise.resolve(searchTimeOptions(term).map((o) => ({ id: o.value, primary: o.label })))}
                onSelect={(o) => setForm({ ...form, time: o.id })}
              />
            </Field>

            {/* The required marker sits on the LABEL, not the Field: a Field's
                `required` reaches every Checkbox inside it, and each step then
                carried its own red asterisk (found on screen, 2026-09-30). */}
            <Field label={{ children: 'Steps', required: true }}>{stepChecks}</Field>
          </>
        )}

        {/* A TRIGGERED NOTIFICATION HAS NO SCHEDULE (D-391): the days, the time
            and the Steps label give way to one question over the same steps —
            those where this person holds an Invoices role. */}
        {form.kind === 'triggered' && (
          <Field label={{ children: 'Notify me when an invoice arrives at', required: true }}>{stepChecks}</Field>
        )}

        {formError && (
          <MessageBar intent="error">
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        )}

        <div className={s.formActions}>
          {/* ON EDIT ONLY, as on every editor: a new notification is Active (D-201). */}
          {editing && <StatusToggle value={active} onChange={setActive} disabled={saving} />}
          <div className={s.formActionsEnd}>
            <Button appearance="secondary" disabled={saving} onClick={() => setMode('list')}>
              Cancel
            </Button>
            <Button appearance="primary" disabled={saving} icon={saving ? <Spinner size="tiny" /> : undefined} onClick={save}>
              Save
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---- list view ----
  const stepLabel = (key: string) => TIMELINE_NODES.find((n) => n.key === key)?.label ?? key
  return (
    <div className={s.root}>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {subs === null ? (
        <Spinner size="small" label="Loading your custom notifications…" />
      ) : subs.length === 0 ? (
        <Text className={s.empty}>You don’t have any custom notifications yet.</Text>
      ) : (
        <Table size="small" aria-label="Your custom notifications" className={mergeClasses(roomy, s.table)}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: '26%' }}>Title</TableHeaderCell>
              <TableHeaderCell style={{ width: '26%' }}>Schedule</TableHeaderCell>
              <TableHeaderCell>Steps</TableHeaderCell>
              <TableHeaderCell style={{ width: '104px' }}>Status</TableHeaderCell>
              <TableHeaderCell style={{ width: '48px' }} aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {subs.map((row) => {
              const isActive = isActiveSubscription(row)
              const busy = busyId === row.id
              const name = row.title || 'Custom Notification'
              return (
                // The row opens the notification to edit, as a row does on every other list.
                <TableRow key={row.id} className={clickable} {...rowActivationProps(() => openEdit(row), `Edit: ${name}`)}>
                  <TableCell>{name}</TableCell>
                  <TableCell>{formatSchedule(row)}</TableCell>
                  <TableCell>
                    <span className={s.stepList}>
                      {(row.steps ?? []).filter(Boolean).map((key) => (
                        <span key={key}>{stepLabel(key)}</span>
                      ))}
                    </span>
                  </TableCell>
                  {/* THE RULES LISTS' STATUS, a dot and the word (owner,
                      2026-10-14): Active or Inactive, set by the editor's toggle. */}
                  <TableCell>
                    <StatusDot active={isActive} />
                  </TableCell>
                  <TableCell>
                    {/* Its own click only: it does not open the row. Active and
                        Inactive are the editor's toggle now (D-361); the list's
                        Pause and Resume went with that. */}
                    <span className={s.rowActions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<DeleteRegular />}
                        disabled={busy}
                        aria-label="Remove"
                        title="Remove"
                        onClick={() => {
                          setRemovingShown(row)
                          setRemoving(row)
                        }}
                      />
                    </span>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}

      {/* No icon in the button, and at the right (owner, 2026-10-14). Every
          word on this screen says "custom notification", not "reminder" (owner, same
          day). */}
      <div className={s.listActions}>
        <Button appearance="primary" onClick={openNew}>
          Add Custom Notification
        </Button>
      </div>
      <DecisionDialog
        open={removing !== null}
        title="Are you sure?"
        body={`The custom notification “${removingShown?.title || 'Custom Notification'}” will be removed, and its emails will stop.`}
        requireReason={false}
        confirmLabel="Remove"
        submitting={busyId !== null}
        onCancel={() => setRemoving(null)}
        onConfirm={() => void confirmRemove()}
      />
    </div>
  )
}
